import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import { useLang } from "@/i18n";

const HAT_IMGS = [
  "/images/hat-baseball.png",
  "/images/hat-snapback.png",
  "/images/hat-bucket.png",
  "/images/hat-beanie.png",
  "/images/hat-boater.png",
  "/images/hat-la-brown.png",
  "/images/hat-green.png",
  "/images/hat-red-cord.png",
];

const Shelf = ({ hats, offset, active, setActive }) => (
  <div className="relative mx-auto max-w-3xl">
    <div className="flex items-end justify-center gap-8 px-8 sm:gap-12 md:gap-16">
      {hats.map((h, i) => {
        const gi = offset + i;
        const isActive = active === gi;
        return (
          <div key={h.name} className="relative flex flex-col items-center">
            <AnimatePresence>
              {isActive && (
                <motion.span
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.18 }}
                  data-testid={`hat-caption-${gi}`}
                  className="absolute -top-9 z-30 whitespace-nowrap rounded-full border border-ink/10 bg-bone/95 px-4 py-1.5 text-xs font-semibold tracking-wide text-ink shadow-[0_8px_24px_rgba(0,0,0,0.10)]"
                >
                  {h.name}
                </motion.span>
              )}
            </AnimatePresence>
            <motion.button
              onMouseEnter={() => setActive(gi)}
              onMouseLeave={() => setActive(null)}
              onClick={(e) => {
                e.stopPropagation();
                setActive(isActive ? null : gi);
              }}
              animate={{ y: isActive ? -10 : 0, scale: isActive ? 1.1 : 1, zIndex: isActive ? 20 : 1 }}
              transition={{ type: "spring", stiffness: 320, damping: 22 }}
              data-testid={`hat-item-${gi}`}
              aria-label={h.name}
              className="relative block cursor-pointer bg-transparent p-0"
            >
              <img
                src={h.img}
                alt={h.name}
                loading="lazy"
                draggable="false"
                className="h-24 w-auto object-contain drop-shadow-[0_10px_10px_rgba(0,0,0,0.18)] sm:h-28 md:h-32"
              />
            </motion.button>
          </div>
        );
      })}
    </div>
    <div className="relative h-2.5 rounded-sm bg-gradient-to-b from-[#8a6b46] via-[#6f5433] to-[#54401f] shadow-[0_6px_14px_rgba(0,0,0,0.25)]" aria-hidden="true">
      <span className="absolute -bottom-3 start-6 h-3 w-1.5 bg-[#54401f]" />
      <span className="absolute -bottom-3 end-6 h-3 w-1.5 bg-[#54401f]" />
    </div>
  </div>
);

const Hats = () => {
  const { t } = useLang();
  const [active, setActive] = useState(null);
  const hats = t.hats.items.map((name, i) => ({ name, img: HAT_IMGS[i] }));

  return (
    <section
      id="hats"
      data-testid="hats-section"
      className="border-b border-ink/10 py-20 md:py-24"
      onClick={() => setActive(null)}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Reveal className="text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-clay">{t.hats.overline}</p>
          <h2 data-testid="hats-heading" className="mt-5 font-serif text-3xl tracking-tight text-ink sm:text-4xl">
            {t.hats.h2a} <em className="italic text-clay">{t.hats.h2b}</em>
          </h2>
          <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-ink/55">{t.hats.sub}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-14 flex flex-col gap-16">
            <Shelf hats={hats.slice(0, 4)} offset={0} active={active} setActive={setActive} />
            <Shelf hats={hats.slice(4)} offset={4} active={active} setActive={setActive} />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Hats;
